import * as THREE from 'three';

// Shared art direction: a warm, low-poly, hand-built look.
// Flat-shaded matte materials, earthy palette, golden-hour light.
export const PALETTE = {
  grass: 0x6f9a3c,
  grassDark: 0x55802e,
  grassLight: 0x8fb04a,
  dirt: 0x9a7650,
  dirtDark: 0x7a5a3a,
  sand: 0xc8b07a,
  stone: 0x9a948a,
  stoneDark: 0x6f6a62,
  stoneLight: 0xb8b2a6,
  mossStone: 0x7f8a68,
  wood: 0x8a5a34,
  woodDark: 0x5e3b22,
  woodLight: 0xb3804e,
  thatch: 0xc9a55a,
  hay: 0xd8b860,
  cloth: 0xd9c9a3,
  iron: 0x4a4a4e,
  rock: 0x857f78,
  snow: 0xf1efe8,
  water: 0x3f7f9a,
  foliage: 0x4f7d2e,
  foliageDark: 0x3a6224,
  foliageAutumn: 0xc07a2e,
  pine: 0x2f5a35,
  lanternGlow: 0xffb35c
};

const materialCache = new Map();

// Matte flat-shaded material, cached by colour so the scene shares materials.
export function matte(color, opts = {}) {
  const key = `${color}|${opts.roughness ?? 0.9}|${opts.emissive ?? ''}|${opts.emissiveIntensity ?? ''}|${opts.vertexColors ? 1 : 0}`;
  if (!opts.unique && materialCache.has(key)) return materialCache.get(key);
  const material = new THREE.MeshStandardMaterial({
    color,
    roughness: opts.roughness ?? 0.9,
    metalness: opts.metalness ?? 0.0,
    flatShading: true,
    vertexColors: !!opts.vertexColors,
    emissive: opts.emissive ?? 0x000000,
    emissiveIntensity: opts.emissiveIntensity ?? 1
  });
  if (!opts.unique) materialCache.set(key, material);
  return material;
}

// Soften the materials of an existing model so it sits in the stylised world:
// flat shading, mostly matte, very little metal. Emissive glows are kept.
export function stylize(object) {
  if (!object) return;
  object.traverse(child => {
    if (!child.isMesh) return;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    for (const material of materials) {
      if (!material || !material.isMeshStandardMaterial || material.userData.stylized) continue;
      material.flatShading = true;
      material.metalness = Math.min(material.metalness, 0.25);
      material.roughness = Math.max(material.roughness, 0.6);
      material.userData.stylized = true;
      material.needsUpdate = true;
    }
  });
}

// Deterministic pseudo-random generator so scenery is identical for every player.
export function seededRandom(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Smooth 2D value noise in roughly [-1, 1].
function hash2(x, y) {
  const h = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return h - Math.floor(h);
}

export function noise2(x, y) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = hash2(xi, yi);
  const b = hash2(xi + 1, yi);
  const c = hash2(xi, yi + 1);
  const d = hash2(xi + 1, yi + 1);
  return (a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v) * 2 - 1;
}

export function fbm(x, y, octaves = 4) {
  let sum = 0;
  let amp = 0.5;
  let freq = 1;
  for (let i = 0; i < octaves; i++) {
    sum += noise2(x * freq, y * freq) * amp;
    freq *= 2;
    amp *= 0.5;
  }
  return sum;
}

// Jitter the vertices of a geometry for a chiselled, hand-made silhouette.
export function jitter(geometry, amount, rand = Math.random) {
  const geo = geometry.index ? geometry.toNonIndexed() : geometry;
  const pos = geo.attributes.position;
  // Move shared corners together so faces stay closed
  const offsets = new Map();
  for (let i = 0; i < pos.count; i++) {
    const key = `${pos.getX(i).toFixed(3)},${pos.getY(i).toFixed(3)},${pos.getZ(i).toFixed(3)}`;
    if (!offsets.has(key)) {
      offsets.set(key, [(rand() - 0.5) * amount, (rand() - 0.5) * amount, (rand() - 0.5) * amount]);
    }
    const [dx, dy, dz] = offsets.get(key);
    pos.setXYZ(i, pos.getX(i) + dx, pos.getY(i) + dy, pos.getZ(i) + dz);
  }
  geo.computeVertexNormals();
  return geo;
}

// Lets an object fade out when it stands between the camera and the player.
// Materials are cloned per object so fading one wall doesn't fade them all.
export class Fader {
  constructor(object) {
    this.object = object;
    this.opacity = 1;
    this.target = 1;
    this.materials = [];
    object.traverse(child => {
      if (!child.isMesh || child.userData.noFade) return;
      const swap = m => {
        const clone = m.clone();
        clone.userData.baseOpacity = m.opacity;
        clone.userData.baseTransparent = m.transparent;
        this.materials.push(clone);
        return clone;
      };
      child.material = Array.isArray(child.material) ? child.material.map(swap) : swap(child.material);
    });
    this.box = new THREE.Box3().setFromObject(object);
  }

  update(deltaTime) {
    if (Math.abs(this.opacity - this.target) < 0.001) return;
    const rate = Math.min(1, deltaTime * 8);
    this.opacity += (this.target - this.opacity) * rate;
    if (Math.abs(this.opacity - this.target) < 0.01) this.opacity = this.target;
    const faded = this.opacity < 0.999;
    for (const m of this.materials) {
      m.transparent = faded || m.userData.baseTransparent;
      m.opacity = m.userData.baseOpacity * this.opacity;
      m.depthWrite = !faded;
      if (m.userData.wasFaded !== faded) {
        m.userData.wasFaded = faded;
        m.needsUpdate = true;
      }
    }
  }

  dispose() {
    this.materials.forEach(m => m.dispose());
    this.materials = [];
  }
}

// Build a piece of geometry with a baked vertex colour, ready for merging.
// Colour variation is what gives merged low-poly props their hand-made look.
export function colored(geometry, color, { x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0, scale = null } = {}) {
  const geo = geometry.index ? geometry.toNonIndexed() : geometry;
  if (scale) geo.scale(scale[0], scale[1], scale[2]);
  if (rx) geo.rotateX(rx);
  if (rz) geo.rotateZ(rz);
  if (ry) geo.rotateY(ry);
  geo.translate(x, y, z);
  const c = color instanceof THREE.Color ? color : new THREE.Color(color);
  const colors = new Float32Array(geo.attributes.position.count * 3);
  for (let i = 0; i < colors.length; i += 3) {
    colors[i] = c.r;
    colors[i + 1] = c.g;
    colors[i + 2] = c.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  // Strip UVs so every merged part has identical attributes
  geo.deleteAttribute('uv');
  return geo;
}

// A colour nudged in lightness/hue for natural variation.
export function vary(color, rand, lightness = 0.06, hue = 0.01) {
  return new THREE.Color(color).offsetHSL((rand() - 0.5) * hue, 0, (rand() - 0.5) * lightness);
}
